import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ExistingCompanyPicker } from "../../components/ExistingCompanyPicker";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { loadIntakeDraft, isDraftDirty } from "../../lib/intakeDraftStorage";
import { mapIntakeMvpToCreateProject } from "../../lib/mapIntakeMvpToCreateProject";
import { useCreateCompany } from "../../lib/queries/companies";
import { useCreateProject, useCourtierParDefaut } from "../../lib/queries/projects";
import { useCreateQuoteRequest } from "../../lib/queries/quoteRequests";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

export function StartRequestPage() {
  const [params] = useSearchParams();
  return <StartRequestContent key={params.get("company_id") ?? ""} />;
}

function StartRequestContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [company, setCompany] = useState<string | undefined>(
    () => params.get("company_id") || undefined,
  );
  const [legacyDraft] = useState(() => loadIntakeDraft("mvp"));
  const [addCompany, setAddCompany] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [createdProject, setCreatedProject] = useState<string>();
  const broker = useCourtierParDefaut(company);
  const companyCreate = useCreateCompany();
  const projectCreate = useCreateProject();
  const requestCreate = useCreateQuoteRequest();
  const busy = projectCreate.isPending || requestCreate.isPending || companyCreate.isPending;
  const { allowNavigation } = useUnsavedChangesGuard(
    !!(
      title ||
      companyName ||
      createdProject ||
      company !== (params.get("company_id") || undefined)
    ),
    t("preparation_context.unsaved"),
    "company_id",
  );
  function showError(err: unknown) {
    setError(
      err instanceof ApiException
        ? getApiErrorMessage(err, normalizeLang(i18n.language))
        : t("errors.generic"),
    );
  }
  async function createCompany(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const row = await companyCreate.mutateAsync({ legal_name: companyName.trim() });
      setCompany(row.id);
      setAddCompany(false);
    } catch (err) {
      showError(err);
    }
  }
  async function next() {
    if (!company || !title.trim() || busy) return;
    setError(null);
    try {
      // Si la seconde création échoue, la reprise réutilise le dossier déjà créé.
      const projectId =
        createdProject ??
        (
          await projectCreate.mutateAsync(
            mapIntakeMvpToCreateProject({
              company_id: company,
              broker_id: broker.data?.broker_id ?? undefined,
              title: title.trim(),
            }),
          )
        ).id;
      setCreatedProject(projectId);
      const request = await requestCreate.mutateAsync({ input: { project_id: projectId } });
      allowNavigation();
      navigate(`/projects/${projectId}?view=preparation&request=${request.id}`);
    } catch (err) {
      showError(err);
    }
  }
  return (
    <div className="mx-auto w-full space-y-4">
      <header className="pt-4">
        <h1 className="text-3xl font-semibold">{t("nav.new_request")}</h1>
        <p className="mt-2 text-slate-600">{t("request_flow.client_help")}</p>
      </header>
      {legacyDraft && isDraftDirty(legacyDraft.data) && (
        <Link
          to="/projects/new"
          className="text-brand-700 inline-flex min-h-11 items-center underline"
        >
          {t("foundation.resume_legacy")}
        </Link>
      )}
      <fieldset disabled={busy || !!createdProject} className="space-y-4">
        <label
          className="block"
          htmlFor="request-company"
          data-guide-target={!company ? "start" : undefined}
        >
          <span className="label">{t("projects.fields.company")}</span>
          <ExistingCompanyPicker id="request-company" value={company} onChange={setCompany} />
        </label>
        {!addCompany && (
          <button type="button" className="btn min-h-11" onClick={() => setAddCompany(true)}>
            {t("request_flow.add_company")}
          </button>
        )}
        {addCompany && (
          <form onSubmit={(e) => void createCompany(e)} className="space-y-3 rounded-lg border p-4">
            <label className="block">
              {t("request_flow.company_name")}
              <input
                className="input"
                required
                name="legal_name"
                autoComplete="organization"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </label>
            <button className="btn-primary min-h-11" type="submit">
              {t("common.save")}
            </button>
            <button className="btn min-h-11" type="button" onClick={() => setAddCompany(false)}>
              {t("common.cancel")}
            </button>
          </form>
        )}
        <label className="block" data-guide-target={company ? "start" : undefined}>
          <span className="label">{t("request_flow.file_title")}</span>
          <input
            className="input"
            name="file_title"
            autoComplete="off"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>
      </fieldset>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      <div className="workspace-action-bar">
        <button
          type="button"
          onClick={() => void next()}
          disabled={!company || !title.trim() || busy}
          className="btn-primary min-h-11"
        >
          {t("request_flow.continue_authorization")}
        </button>
      </div>
    </div>
  );
}
